/**
 * Динамическая карточка заявки РД по опубликованному шаблону (GET …/rd-requests/{id}/card).
 */

import { type FormEvent, useEffect, useMemo, useState } from "react";
import type { RdRequestCardFieldValue, RdRequestCardResponse, RdRequestMeta } from "@/types/rd-request";
import type { RdCardTemplateSection } from "@/types/rd-card-template";
import {
  CardRuntimeSectionLayout,
  CARD_RUNTIME_FIELD_GRID_CLASS,
  CardScalarControl,
  formatReadonlyScalar,
  isWideCardFieldType,
  MetaKlsSelect,
} from "@/components/card/shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export interface RdDynamicCardRendererProps {
  card: RdRequestCardResponse;
  meta?: RdRequestMeta | null;
  saving?: boolean;
  /** Если false — только просмотр (нет кнопки сохранения). */
  canEdit?: boolean;
  onSave: (values: Record<string, unknown>) => void;
}

export function RdDynamicCardRenderer({
  card,
  meta,
  saving,
  canEdit = true,
  onSave,
}: RdDynamicCardRendererProps) {
  const [local, setLocal] = useState<Record<string, unknown>>({});

  useEffect(() => {
    const next: Record<string, unknown> = {};
    for (const row of card.field_values) {
      if (row.field_type === "boolean") {
        next[row.field_key] = Boolean(row.value);
      } else {
        next[row.field_key] = row.value ?? "";
      }
    }
    setLocal(next);
  }, [card]);

  const sectionsSorted = useMemo(
    () =>
      [...(card.template?.sections ?? [])].sort((a, b) => a.display_order - b.display_order),
    [card.template?.sections],
  );

  const hasWritableFields = useMemo(
    () => card.field_values.some((r) => !r.is_readonly),
    [card.field_values],
  );

  const rowsForSection = (sec: RdCardTemplateSection | null) =>
    card.field_values.filter((f) => {
      if (sec == null) return f.section_id == null;
      return f.section_id === sec.id;
    });

  function setField(key: string, value: unknown) {
    setLocal((prev) => ({ ...prev, [key]: value }));
  }

  function renderControl(row: RdRequestCardFieldValue) {
    const value = local[row.field_key];

    if (row.is_readonly) {
      const noStorageBinding =
        row.target_column == null || String(row.target_column).trim() === "";
      return (
        <div className="text-sm rounded-md border bg-muted/40 px-3 py-2 min-h-[36px]">
          {formatReadonlyScalar(value)}
          {noStorageBinding ? (
            <p className="text-xs text-muted-foreground mt-1.5">
              Поле только для чтения в шаблоне. Редактируемые поля без колонки req сохраняются в универсальном
              хранилище атрибутов заявки.
            </p>
          ) : null}
        </div>
      );
    }

    const tc = row.target_column ?? "";

    if (
      (row.field_type === "classifier" || row.field_type === "classifier_tree") &&
      tc === "kls_id_grif" &&
      meta?.secrecy_grifs?.length
    ) {
      return (
        <MetaKlsSelect
          value={value}
          onChange={(n) => setField(row.field_key, n)}
          options={meta.secrecy_grifs}
        />
      );
    }

    if (
      (row.field_type === "classifier" || row.field_type === "classifier_tree") &&
      tc === "kls_id_treq" &&
      meta?.territory_request_types?.length
    ) {
      return (
        <MetaKlsSelect
          value={value}
          onChange={(n) => setField(row.field_key, n)}
          options={meta.territory_request_types}
        />
      );
    }

    return (
      <CardScalarControl
        fieldType={row.field_type}
        id={row.field_key}
        value={value}
        onChange={(next) => setField(row.field_key, next)}
        booleanVariant="checkbox"
      />
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const writable: Record<string, unknown> = {};
    for (const row of card.field_values) {
      if (row.is_readonly) continue;
      writable[row.field_key] = local[row.field_key];
    }
    onSave(writable);
  }

  const orphanRows = rowsForSection(null);

  const fieldColClass = (ft: string) => (isWideCardFieldType(ft) ? "md:col-span-2" : "");

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {card.message ? <p className="text-sm text-muted-foreground">{card.message}</p> : null}

      {sectionsSorted.map((sec) => {
        const rows = rowsForSection(sec);
        if (rows.length === 0) return null;

        const inner = (
          <div className={CARD_RUNTIME_FIELD_GRID_CLASS}>
            {rows.map((row) => (
              <div key={row.field_key} className={fieldColClass(row.field_type)}>
                <Label className="mb-1 block">{row.label}</Label>
                {renderControl(row)}
              </div>
            ))}
          </div>
        );

        return (
          <CardRuntimeSectionLayout
            key={sec.id}
            title={sec.name}
            isCollapsible={sec.is_collapsible}
            defaultOpen={sec.is_default_open}
            staticRootExtraClassName="space-y-2"
          >
            {inner}
          </CardRuntimeSectionLayout>
        );
      })}

      {orphanRows.length > 0 ? (
        <CardRuntimeSectionLayout
          title="Без секции"
          isCollapsible={false}
          staticTitleClassName="text-sm font-medium text-muted-foreground"
          staticRootExtraClassName="space-y-2"
        >
          <div className={CARD_RUNTIME_FIELD_GRID_CLASS}>
            {orphanRows.map((row) => (
              <div key={row.field_key} className={fieldColClass(row.field_type)}>
                <Label className="mb-1 block">{row.label}</Label>
                {renderControl(row)}
              </div>
            ))}
          </div>
        </CardRuntimeSectionLayout>
      ) : null}

      {canEdit && hasWritableFields ? (
        <Button type="submit" disabled={saving}>
          {saving ? "Сохранение..." : "Сохранить"}
        </Button>
      ) : null}
    </form>
  );
}
